'use client';

import type { Dispatch } from 'react';

import { useUser } from '@auth0/nextjs-auth0';
import { useMemo, useEffect, useContext, useReducer, createContext } from 'react';

import { useCurrentTestelopeUserQuery } from 'src/hooks/queries/testelope-user';

import { TestelopUserReducer } from './TestelopeUserReducer';

import type { TestelopeUserState, TestelopeUserDispatch } from './TestelopeUserTypes';

interface TestelopeUserContextProps extends TestelopeUserState {
  testelopeUserDispatch: Dispatch<TestelopeUserDispatch>;
}

const TestelopeUserContext = createContext<TestelopeUserContextProps>(
  {} as TestelopeUserContextProps
);

export function TestelopeUserProvider({ children }: any) {
  const [state, testelopeUserDispatch] = useReducer(TestelopUserReducer, {});
  const { user, isLoading } = useUser();
  const {
    data: currentTestelopeUser,
    isLoading: isCurrentUserLoading,
    error,
  } = useCurrentTestelopeUserQuery(Boolean(user && !isLoading));

  useEffect(() => {
    if (currentTestelopeUser) {
      testelopeUserDispatch({
        type: 'TESTELOPE_USER',
        value: {
          testelopeUser: currentTestelopeUser,
        },
      });
    }
  }, [currentTestelopeUser, testelopeUserDispatch]);

  useEffect(() => {
    if (error) {
      console.error('Error fetching Testelope user data:', error);
    }
  }, [error]);

  return (
    <TestelopeUserContext.Provider
      value={useMemo(
        () => ({
          testelopeUserDispatch,
          testelopeUser: state.testelopeUser,
          isLoading: isCurrentUserLoading,
        }),
        [testelopeUserDispatch, state.testelopeUser, isCurrentUserLoading]
      )}
    >
      {children}
    </TestelopeUserContext.Provider>
  );
}

export const useTestelopeUserContext = () => {
  const context = useContext(TestelopeUserContext);
  if (!context) {
    throw new Error('useTestelopeUser must be used within a TestelopeUserProvider');
  }
  return context;
};
